import * as R from 'ramda';

import { TReduxActions } from '../../actions';
import { EFavoritesActionType } from '../../actions/favorites';
import { EFeaturesActionType } from '../../actions/features';
import { EResultsActionType } from '../../actions/results';
import { EMarkAsViewedActionType } from '../../actions/viewed';
import { prepareFeatures } from '../../mappers/features';
import { IFeaturesMap, ITilesMap, TFeature } from '../../types/map';
import { IPrecisionFeatures } from '../../types/redux/store/features';

const defaultState: IPrecisionFeatures = {};

function mergeFeaturesWithMainPinPriority(
  currentFeatures: IFeaturesMap,
  extendedFeatures: TFeature[],
  filteredFeatures: TFeature[],
): IFeaturesMap {
  const features = [...Object.values(currentFeatures), ...extendedFeatures, ...filteredFeatures];
  const extendedCoordinates = new Set(
    features.filter(feature => feature.properties.isExtended).map(feature => feature.geometry.coordinates.join(',')),
  );
  const mainPinCoordinates = new Set(
    features
      .filter(feature => feature.properties.isNewbuildingMainPin)
      .map(feature => feature.geometry.coordinates.join(',')),
  );

  return features.reduce<IFeaturesMap>((result, feature) => {
    const coordinates = feature.geometry.coordinates.join(',');
    const hasMainPinAtSameCoordinates = mainPinCoordinates.has(coordinates);

    if (feature.properties.isExtended && !feature.properties.isNewbuildingMainPin && hasMainPinAtSameCoordinates) {
      return result;
    }

    result[feature.id] =
      feature.properties.isNewbuildingMainPin && extendedCoordinates.has(coordinates)
        ? { ...feature, properties: { ...feature.properties, isExtended: true } }
        : feature;

    return result;
  }, {});
}

export function featuresReducer(state: IPrecisionFeatures = defaultState, action: TReduxActions): IPrecisionFeatures {
  switch (action.type) {
    case EResultsActionType.Succeed: {
      const { clean } = action.payload;
      const tiles = action.payload.tiles || [];
      const precision = action.payload.precision as number;

      const extended = prepareFeatures(action.payload.extended || [], { isExtended: true });
      const filtered = prepareFeatures(action.payload.filtered || []);

      if (clean) {
        const uniqueFeatures = mergeFeaturesWithMainPinPriority({}, extended, filtered);
        const uniqueTiles: ITilesMap = {};

        tiles.forEach(([x, y]) => {
          uniqueTiles[`${x},${y}`] = [x, y];
        });

        return {
          [precision]: {
            features: uniqueFeatures,
            tiles: uniqueTiles,
          },
        };
      }

      const uniqueFeatures = mergeFeaturesWithMainPinPriority(
        R.pathOr({}, [precision, 'features'], state),
        extended,
        filtered,
      );
      const uniqueTiles: ITilesMap = { ...R.pathOr({}, [precision, 'tiles'], state) };

      tiles.forEach(([x, y]) => {
        uniqueTiles[`${x},${y}`] = [x, y];
      });

      return {
        ...state,
        [precision]: {
          ...state[precision],
          features: uniqueFeatures,
          tiles: uniqueTiles,
        },
      };
    }

    case EFavoritesActionType.Added: {
      const { currentPrecision, selectedFeatureId } = action.payload;

      if (state[currentPrecision].features[selectedFeatureId]) {
        return {
          [currentPrecision]: {
            ...state[currentPrecision],
            features: {
              ...state[currentPrecision].features,
              [selectedFeatureId]: {
                ...state[currentPrecision].features[selectedFeatureId],
                properties: {
                  ...state[currentPrecision].features[selectedFeatureId].properties,
                  favoriteIds: [
                    ...(state[currentPrecision].features[selectedFeatureId].properties.favoriteIds || []),
                    action.payload.offerId,
                  ],
                },
              },
            },
          },
        };
      }

      return state;
    }

    case EFavoritesActionType.Removed: {
      const { currentPrecision, selectedFeatureId } = action.payload;

      if (state[currentPrecision].features[selectedFeatureId]) {
        return {
          [currentPrecision]: {
            ...state[currentPrecision],
            features: {
              ...state[currentPrecision].features,
              [selectedFeatureId]: removeFavoriteFromFeature(
                state[currentPrecision].features[selectedFeatureId],
                action.payload.offerId,
              ),
            },
          },
        };
      }

      return state;
    }

    case EMarkAsViewedActionType.Succeed: {
      const { precision, featureId } = action.payload;

      if (state[precision] && state[precision].features[featureId]) {
        return {
          [precision]: {
            ...state[precision],
            features: {
              ...state[precision].features,
              [featureId]: {
                ...state[precision].features[featureId],
                properties: {
                  ...state[precision].features[featureId].properties,
                  isViewed: true,
                },
              },
            },
          },
        };
      }

      return state;
    }

    case EFeaturesActionType.FeaturePropertiesUpdated: {
      const { precision, featureId, properties } = action.payload;

      if (state[precision] && state[precision].features[featureId]) {
        return {
          ...state,
          [precision]: {
            ...state[precision],
            features: {
              ...state[precision].features,
              [featureId]: {
                ...state[precision].features[featureId],
                properties: {
                  ...state[precision].features[featureId].properties,
                  ...properties,
                },
              },
            },
          },
        };
      }

      return state;
    }

    default:
      return state;
  }
}

function removeFavoriteFromFeature(feature: TFeature, offerId: number): TFeature {
  let nextFavoriteIds = feature.properties.favoriteIds;
  if (!nextFavoriteIds) {
    return feature;
  }

  nextFavoriteIds = nextFavoriteIds.filter(oid => oid !== offerId);
  if (nextFavoriteIds.length === 0) {
    nextFavoriteIds = undefined;
  }

  return {
    ...feature,
    properties: {
      ...feature.properties,
      favoriteIds: nextFavoriteIds,
    },
  };
}
